<template>
    <page-content
        :loading="isLoading"
        :error="error"
    >
        <v-container>
            <v-row align="center" justify="center">
                <v-col xs="12" md="6" lg="6" class="pa-4">
                    <v-card class="pa-3">
                        <div class="d-flex justify-center mb-1 p-relative">
                            <div
                                v-if="canEdit"
                                class="edit-bio-button"
                            >
                                <edit-bio-dialog
                                    :bio="bio"
                                    @save="onBioSave"
                                />
                            </div>
                            <div class="p-relative">
                                <v-avatar
                                    width="100"
                                    height="100"
                                >
                                    <template v-if="bio.userImage">
                                        <v-img
                                            :src="bio.userImage"
                                        />
                                    </template>
                                    <template v-else>
                                        <v-icon size="100" color="primary">
                                            insert_emoticon
                                        </v-icon>
                                    </template>
                                </v-avatar>
                                <v-dialog
                                    v-if="canEdit"
                                    v-model="photoDialog.show"
                                    :fullscreen="$vuetify.breakpoint.smAndDown"
                                    :max-width="500"
                                    :transition="$vuetify.breakpoint.smAndDown
                                        ? 'dialog-bottom-transition'
                                        : 'fade-transition'"
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            class="edit-photo-button"
                                            icon
                                            absolute
                                            right
                                            bottom
                                            elevation="2"
                                            v-on="on"
                                        >
                                            <v-icon>edit</v-icon>
                                        </v-btn>
                                    </template>
                                    <edit-photo-form
                                        :show.sync="photoDialog.show"
                                        :is-saving.sync="photoDialog.isSaving"
                                        @save="onPhotoSave"
                                    />
                                </v-dialog>
                            </div>
                        </div>
                        <div class="display-1 text-center mb-3">
                            @{{ handle }}
                        </div>
                        <!-- Bio -->
                        <div class="text-center mb-3">
                            <p>
                                {{ bio.profileName }}
                            </p>
                            <p>
                                {{ bio.description }}
                            </p>
                        </div>
                        <v-divider class="py-3" />
                        <!-- Links -->
                        <div
                            v-if="canEdit"
                            class="d-flex mb-3"
                        >
                            <v-spacer />
                            <v-btn
                                icon
                                :to="{name: 'editLinks'}"
                            >
                                <v-icon>
                                    edit
                                </v-icon>
                            </v-btn>
                        </div>
                        <div
                            v-for="(item, index) in links"
                            :key="index"
                            class="mb-2"
                        >
                            <link-button
                                :link="item"
                            />
                        </div>
                        <div
                            v-if="links.length === 0"
                            class="text-center"
                        >
                            {{ canEdit ? 'No Links found. Make one today!' : 'This user does not currently have any links.' }}
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
        <!-- Custom Error for user not found -->
        <template
            v-if="error === NOT_FOUND_ERROR"
            v-slot:error
        >
            <v-container>
                <v-row>
                    <v-col
                        cols="12"
                        sm="6"
                    >
                        <v-img
                            class="my-5"
                            :src="require('../../assets/blank_canvas.svg')"
                        />
                    </v-col>
                    <v-col
                        class="text-center text-sm-left"
                        cols="12"
                        sm="6"
                    >
                        <div class="d-flex flex-column fill-height justify-center">
                            <h1>User Not Found</h1>
                            <p class="body-1">
                                Uh Oh! Looks like there is no one here by that name. Sign up today to claim this name!
                            </p>
                            <div>
                                <v-btn
                                    color="primary"
                                    :to="{ name: 'signUp' }"
                                >
                                    Sign Up
                                </v-btn>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-container>
        </template>
    </page-content>
</template>

<script>
import axios from 'axios'
import EditBioDialog from './EditBioDialog'
import LinkButton from './LinkButton'
import EditPhotoForm from './EditPhotoForm'
import UserBus from '../../utils/buses/UserBus'
import { defaultError } from '../../utils/constants/errors'

const NOT_FOUND_ERROR = 'notFound'

export default {
    components: {
        EditBioDialog,
        LinkButton,
        EditPhotoForm
    },
    data () {
        return {
            isLoading: true,
            error: null,
            handle: null,
            links: [],
            bio: {
                profileName: null,
                description: null,
                userImage: null
            },
            userNotFound: false,
            NOT_FOUND_ERROR,
            photoDialog: {
                show: false,
                isSaving: false
            }
        }
    },
    computed: {
        canEdit: function () {
            return (
                UserBus.user
                    && UserBus.user.handleKey
                    && UserBus.user.handleKey === this.$route.params.handle.toLowerCase()
            )
        }
    },
    created () {
        const handle = this.$route.params.handle
        axios.get('/user/' + handle)
            .then(res => {
                this.handle = res.data.user.handleKey

                this.bio = {
                    profileName: res.data.user.profileName,
                    description: res.data.user.description,
                    userImage: res.data.user.userImage
                }

                document.title = `${this.handle}'s Profile`

                this.links = res.data.user.links
            })
            .catch(err => {
                console.error('Get User Error: ', err)
                if (err.status === 404) {
                    this.error = NOT_FOUND_ERROR
                } else {
                    this.error = defaultError
                }
            })
            .finally(res => {
                this.isLoading = false
            })
    },
    methods: {
        onBioSave (event) {
            this.bio = {
                ...this.bio,
                profileName: event.profileName,
                description: event.description
            }
        },
        onPhotoSave (url) {
            this.bio = {
                ...this.bio,
                // cache bust
                userImage: `${url}?${new Date().getTime()}`
            }
        }
    }
}
</script>

<style scoped lang="scss">
.link-button {
    ::v-deep .v-btn__content {
        max-width: 100%;
    }
}
.edit-bio-button {
    right: 0;
    top: 0;
    position: absolute;
    display: inline-block;
}
.edit-photo-button {
    right: 0;
    bottom: 0;
    position: absolute;
    background: white;
}
</style>
